/**
 * Common free and disposable email domains for client-side validation.
 * The backend Lambda has the comprehensive list — this is for instant UX feedback.
 */
export const BLOCKED_EMAIL_DOMAINS: Set<string> = new Set([
  // --- Google ---
  "gmail.com",
  "googlemail.com",

  // --- Microsoft ---
  "outlook.com",
  "outlook.de",
  "outlook.fr",
  "outlook.es",
  "outlook.it",
  "outlook.co.uk",
  "outlook.jp",
  "outlook.com.br",
  "hotmail.com",
  "hotmail.de",
  "hotmail.fr",
  "hotmail.es",
  "hotmail.it",
  "hotmail.co.uk",
  "hotmail.co.jp",
  "hotmail.com.br",
  "live.com",
  "live.de",
  "live.fr",
  "live.co.uk",
  "live.it",
  "live.nl",
  "msn.com",

  // --- Yahoo ---
  "yahoo.com",
  "yahoo.de",
  "yahoo.fr",
  "yahoo.es",
  "yahoo.it",
  "yahoo.co.uk",
  "yahoo.co.jp",
  "yahoo.com.br",
  "yahoo.ca",
  "yahoo.com.au",
  "yahoo.co.in",
  "ymail.com",
  "rocketmail.com",

  // --- Apple ---
  "icloud.com",
  "me.com",
  "mac.com",

  // --- Proton ---
  "protonmail.com",
  "protonmail.ch",
  "proton.me",
  "pm.me",

  // --- Tutanota ---
  "tutanota.com",
  "tutanota.de",
  "tuta.io",
  "tuta.com",

  // --- German providers ---
  "gmx.de",
  "gmx.net",
  "gmx.com",
  "gmx.at",
  "gmx.ch",
  "web.de",
  "t-online.de",
  "freenet.de",
  "posteo.de",
  "mailbox.org",

  // --- Russian providers ---
  "mail.ru",
  "yandex.ru",
  "yandex.com",
  "rambler.ru",
  "bk.ru",
  "inbox.ru",
  "list.ru",

  // --- AOL ---
  "aol.com",
  "aim.com",

  // --- Zoho ---
  "zoho.com",
  "zohomail.com",

  // --- Other international free providers ---
  "mail.com",
  "email.com",
  "usa.com",
  "myself.com",
  "consultant.com",
  "engineer.com",
  "qq.com",
  "163.com",
  "126.com",
  "sina.com",
  "naver.com",
  "hanmail.net",
  "daum.net",
  "laposte.net",
  "orange.fr",
  "wanadoo.fr",
  "sfr.fr",
  "free.fr",
  "libero.it",
  "virgilio.it",
  "alice.it",
  "tin.it",
  "terra.com.br",
  "uol.com.br",
  "bol.com.br",
  "rediffmail.com",

  // --- US ISP providers ---
  "comcast.net",
  "att.net",
  "sbcglobal.net",
  "verizon.net",
  "cox.net",
  "charter.net",
  "earthlink.net",
  "bellsouth.net",
  "optonline.net",
  "roadrunner.com",
  "centurylink.net",
  "windstream.net",
  "frontier.com",

  // --- Canadian / UK ISPs ---
  "shaw.ca",
  "rogers.com",
  "sympatico.ca",
  "btinternet.com",
  "sky.com",
  "ntlworld.com",
  "talktalk.net",
  "virginmedia.com",

  // --- Other free email services ---
  "fastmail.com",
  "hushmail.com",
  "runbox.com",
  "startmail.com",
  "disroot.org",
  "mailfence.com",

  // --- Disposable / temporary email services ---
  "mailinator.com",
  "guerrillamail.com",
  "guerrillamail.de",
  "guerrillamail.net",
  "grr.la",
  "tempmail.com",
  "temp-mail.org",
  "yopmail.com",
  "yopmail.fr",
  "10minutemail.com",
  "10minute.email",
  "trashmail.com",
  "trashmail.me",
  "trashmail.net",
  "sharklasers.com",
  "guerrillamailblock.com",
  "maildrop.cc",
  "dispostable.com",
  "throwaway.email",
  "getnada.com",
  "mailnesia.com",
  "fakeinbox.com",
  "burnermail.io",
  "mohmal.com",
  "tempail.com",
  "mytemp.email",
  "mailcatch.com",
  "mailnator.com",
  "harakirimail.com",
  "spamgourmet.com",
  "mintemail.com",
  "emailondeck.com",

  // --- Public catch-all mail services (route to a shared public host) ---
  "duep.net",

  // --- mail.tm rotation pool (rotates its public domains; block the whole set) ---
  "wshu.net",
  "deltajohnsons.com",
  "sharebot.net",
  "dollicons.com",
  "virgilian.com",
  "airsworld.net",
  "starmail.net",
  "novaemail.com",
  "carpin.org",
  "inilogic.com",
  "affecting.org",
  "splitparents.com",
  "effobe.com",
  "baybabes.com",
  "maxresistance.com",
  "biojuris.com",
  "nucleant.org",
  "solarunited.org",
  "supersave.net",
  "logicstreak.com",
  "solarunited.net",
  "northsixty.com",
  "workingtall.com",
  "trythe.net",
  "uniromax.com",
  "pussport.com",
  "metalunits.com",
  "leadwizzer.com",
  "alilot.com",
  "frederictonlawyer.com",
  "canfga.org",
  "sinaite.net",
  "midiharmonica.com",
  "cutradition.com",
  "candassociates.com",
  "scpulse.com",
  "knowledgemd.com",
  "arxxwalls.com",
  "emergentvillage.org",
  "spacehotline.com",
  "karenkey.com",
  "nightorb.com",
  "triots.com",
  "eurokool.com",
  "valanides.com",
  "bugfoo.com",
  "internetkeno.com",
  "exelica.com",
  "pretreer.com",
  "diginey.com",
  "hldrive.com",
  "wireconnected.com",
  "desertsundesigns.com",
  "jcnorris.com",
  "lsereborn.com",
  "mitico.org",
  "puabook.com",
  "yogirt.com",
  "awgarstone.com",
  "mailtub.com",
  "fthcapital.com",
  "finacenter.com",
  "navalcadets.com",
  "fastsearcher.com",
  "belgianairways.com",
  "talemarketing.com",
  "rowdydow.com",
  "filmla.org",
  "akirapowered.com",
  "wsypc.com",
  "rustyload.com",
  "livinitlarge.net",
  "thegrampians.net",
  "goeschman.com",
  "soscandia.org",
  "freesourcecodes.com",
  "tohru.org",
  "e-record.com",
  "edny.net",
  "indigobook.com",
  "ptct.net",
  "chefalicious.com",
  "dcpa.net",
  "punkproof.com",
  "mechanicspedia.com",
  "somoj.com",
  "powerscrews.com",
  "tiffincrane.com",
  "2200freefonts.com",
  "comfythings.com",
  "oosln.com",
])
